import drillThroughIcon from '@disy/cadenza-icons/drill-through.svg';
import { assert, assertNonNullable } from '@disy/lib/custom-error';
import { icon } from '@disy/lib/icon';
import type { MenuItemGroup } from '@disy/ui/menu';

import type { Action } from 'cadenza/action';
import { MenuAction } from 'cadenza/actions/menu-action';
import { openDynamicDrillThrough } from 'cadenza/api-client/workbook-drill-through-api';
import type { Map as CadenzaMap } from 'cadenza/map/map';
import { getStore } from 'cadenza/store/cadenza-store';
import type { DrillThroughDto } from 'cadenza/workbook/drill-through/model/drill-through-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { SelectionCartAction } from 'cadenza/workbook/selection-cart/selection-cart-action';
import { getWorkbookNameAndHash } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';

import i18n from './drill-through-action.properties';

/**
 * Create Drill-Through actions for the given destinations.
 *
 * If not possible to drill-through using the current selected values, the destinations in the
 * selection are empty.
 *
 * @param drillThroughDestinations - Dynamic drill-through destinations created via workbook UI that are applicable for the given selection. Empty if no applicable destinations are available.
 * @param dataViewId - The ID of the data view the selection was made in
 * @param selectionIndex - Index to match only subset of workbook selection records. If not passed, whole selection is included in processing drill-through action.
 * @param map - Map instance
 * @return Drill-Through menu items as grouping menu action
 */
export function createDrillThroughActions({
  drillThroughDestinations,
  dataViewId,
  selectionIndex,
  map,
}: {
  drillThroughDestinations: DrillThroughDto[];
  dataViewId: DataViewId;
  selectionIndex?: number;
  map?: CadenzaMap;
}) {
  return new MenuAction(
    () => {
      const drillThroughItems: (Action | MenuItemGroup)[] = [];
      // These come  from the backend sorted by category, then by print name.
      const categoryItemGroups = new Map<string, Action[]>();
      drillThroughDestinations
        .filter((drillThroughDestination) => !!drillThroughDestination.category)
        .filter((drillThroughDestination) => isDrillThroughUiFeatureEnabled(drillThroughDestination))
        .forEach((drillThrough) => {
          if (drillThrough.category && !categoryItemGroups.has(drillThrough.category)) {
            categoryItemGroups.set(drillThrough.category, []);
          }
          if (drillThrough.category) {
            assertNonNullable(categoryItemGroups.get(drillThrough.category), 'Category must be defined.');
            categoryItemGroups
              .get(drillThrough.category)!
              .push(new DrillThroughAction({ dataViewId, drillThrough, selectionIndex, map }));
          }
        });

      categoryItemGroups.forEach((items, category) => {
        drillThroughItems.push({ group: items, label: category });
      });

      // Drill-through without category go last into a catch-all category.
      // This only applies if there are any other categories
      const uncategorized = drillThroughDestinations
        .filter((drillThroughDestination) => !drillThroughDestination.category)
        .filter((drillThroughDestination) => isDrillThroughUiFeatureEnabled(drillThroughDestination))
        .map((drillThrough) => new DrillThroughAction({ dataViewId, drillThrough, selectionIndex, map }));

      if (uncategorized.length === 0) {
        return drillThroughItems;
      }

      if (categoryItemGroups.size === 0) {
        // No category needed, just add them at the end
        uncategorized.forEach((item) => drillThroughItems.push(item));
      } else {
        // For the unlikely case that the user entered a value for category identical
        // to the pre-defined catch-all, add a space to the end of the catch-all.
        const localizedCatchAllCategoryName = i18n('drillThrough.other.category');
        const catchAllCategory =
          categoryItemGroups.has(localizedCatchAllCategoryName) ?
            `${localizedCatchAllCategoryName} `
          : localizedCatchAllCategoryName;
        drillThroughItems.push({ group: uncategorized, label: catchAllCategory });
      }
      return drillThroughItems;
    },
    {
      label: i18n('menu-action.title'),
      icon: icon(drillThroughIcon),
    },
  );
}

/**
 * Checks whether the workbookFeature(s) for the corresponding drillThrough destination is available
 *
 * @param {import('cadenza/workbook/drill-though/dto/drill-through-dto').DrillThroughDto} drillThroughDestination
 */
function isDrillThroughUiFeatureEnabled(drillThroughDestination: DrillThroughDto) {
  const type = drillThroughDestination.type;
  switch (type) {
    case 'EXTERNAL_LINK':
      return isWorkbookFeatureAvailable(getStore().getState(), 'workbook-drillthrough-external-links');
    case 'POST_MESSAGE':
      return isWorkbookFeatureAvailable(getStore().getState(), 'workbook-drillthrough-postmessage');
    case 'VIEW':
      return isViewWorksheetReportDrillThroughUiFeatureAvailable(drillThroughDestination);
    case 'WORKSHEET':
      return isViewWorksheetReportDrillThroughUiFeatureAvailable(drillThroughDestination);
    case 'REPORT':
      return isViewWorksheetReportDrillThroughUiFeatureAvailable(drillThroughDestination);
  }
}

/**
 * Checks whether the workbookFeatures for a view or worksheetDrillThrough is available
 *
 * @param {import('cadenza/workbook/drill-though/dto/drill-through-dto').DrillThroughDto} drillThroughDestination
 */
function isViewWorksheetReportDrillThroughUiFeatureAvailable(drillThroughDestination: DrillThroughDto) {
  assert(
    drillThroughDestination.type === 'VIEW' ||
      drillThroughDestination.type === 'WORKSHEET' ||
      drillThroughDestination.type === 'REPORT',
    "This check can only handle drillThrough destinations of the type 'view', 'worksheet' or 'report'",
  );

  const currentlyOpenedWorkbookNameAndHash = getWorkbookNameAndHash(getStore().getState()).id;
  const drillThroughToSameWorkbook = drillThroughDestination.workbookName === currentlyOpenedWorkbookNameAndHash;
  const sameWorkbookForbidden =
    !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-drillthrough-same-workbook') &&
    drillThroughToSameWorkbook;
  const otherWorkbookForbidden =
    !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-drillthrough-other-workbooks') &&
    !drillThroughToSameWorkbook;
  return !(sameWorkbookForbidden || otherWorkbookForbidden);
}

class DrillThroughAction extends SelectionCartAction {
  /**
   * The ID of the data view the selection was made in.
   */
  readonly #dataViewId;

  /**
   * Dynamic drill-through destinations created via workbook UI that are applicable for the given
   * selection. Empty if no applicable destinations are available.
   */
  readonly #drillThrough;

  /**
   * Dynamic drill-through destinations created via workbook UI that are applicable for the given
   * selection. Dynamic drill-through destinations can also be available in map views via feature info flyout.
   * In the case the actions are created for the feature info flyout, they reference only subset of workbook
   * selection. The subset of selection values which will be used with given drill-through action is defined
   * by this index.
   * In cases the index is undefined, then whole workbook selection is considered when processing drill-through
   * action.
   */
  readonly #selectionIndex;

  readonly #map;

  /**
   * @param {object} options - Options
   * @param options.dataViewId - The ID of the data view the selection was made in.
   * @param options.drillThrough - Dynamic drill-through destinations created via workbook UI that are applicable
   * for the given selection. Empty if no applicable destinations are available.
   * @param options.selectionIndex - Index to match only subset of workbook selection records. If not passed,
   * whole selection is included in processing drill-through action.
   * @param options.map - Map instance
   */
  constructor({
    dataViewId,
    drillThrough,
    selectionIndex,
    map,
  }: {
    dataViewId: DataViewId;
    drillThrough: DrillThroughDto;
    selectionIndex?: number;
    map?: CadenzaMap;
  }) {
    super({
      label: drillThrough.printName,
      icon: icon(drillThroughIcon),
    });
    this.#dataViewId = dataViewId;
    this.#drillThrough = drillThrough;
    this.#selectionIndex = selectionIndex;
    this.#map = map;
  }

  override async execute() {
    super.onExecuteStart();
    await openDynamicDrillThrough(this.#dataViewId, this.#drillThrough, this.#selectionIndex, this.#map);
    super.onExecuteEnd();
  }
}
